import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import apiClient from "../../../../../helpers/api-client";
import { ProctoringAssessmentConfigurationV1, ProctoringOption } from "@verity/proctoring-option/types";
import { FILTER_EVENTS } from "../../../student-selection/videoplayer/Timestamps/Selector/EventTypeSelector";
import { RecordingSourceVideoUrls } from "../../../student-selection/videoplayer/ProctoringVideoPlayer";
import convertEventFormat from "../../convertEventFormat";

export type ProctoringSession = {
	id: number;
	uuid: string;
	studentId: number;
	liveSessionId?: string;
	lmsQuizId: number;
	createdAt: string;
};

export type FlagLevel = 0 | 1 | 2 | 3;

type ProctoringEventsResponse = {
	events: {
		id: number;
		type: string;
		name: string;
		time: string;
		details?: Record<string, unknown>;
		anomalyRef?: number;
		createdAt: string;
	}[];
	anomalies: {
		id: number;
		flagLevel: FlagLevel;
		comment?: string;
		resolved: boolean;
	}[];
};

export type ProctoringEvent = {
	id: number;
	type: string;
	name: string;
	time: number;
	details?: Record<string, unknown>;
	anomaly?: {
		flagLevel: FlagLevel;
		comment?: string;
		resolved: boolean;
	};
	createdAt: string;
};

export type ProctoringEventInfo = {
	events: ProctoringEvent[];
	startProctoringTime?: number;
	startQuizTime?: number;
	endQuizTime?: number;
	endProctoringTime?: number;
	startRecordingTime?: number;
	proctoringOptions?: ProctoringOption & ProctoringAssessmentConfigurationV1;
	sessionEvents: ProctoringEvent[];
};

export type ProctoringEventType = (typeof FILTER_EVENTS)[number]["name"];

export type ProctoringEventQueryOptions = {
	eventTypes: ProctoringEventType[];
	flagLevels: FlagLevel[];
};

export type Course = {
	id: number;
	name?: string;
};

export type Assessment = {
	id: number;
	courseId: number;
	name?: string;
	course?: Course;
};

export type User = {
	id: number;
	name?: string;
	profilePictureUrl?: string;
};

const proctoringSessionApi = createApi({
	reducerPath: "proctoringSession",
	baseQuery: async (arg, api, extraOptions) =>
		fetchBaseQuery({
			fetchFn: apiClient.fetch.bind(apiClient),
			baseUrl: `/api/verity`,
			prepareHeaders: (headers) => {
				headers.set("Content-Type", "application/json");
				return headers;
			},
		})(arg, api, extraOptions),
	tagTypes: ["ProctoringSession", "Events", "Event", "Assessment", "User", "VideoUrls"],
	endpoints: (builder) => ({
		getProctoringSession: builder.query<ProctoringSession, string>({
			query: (id) => `proctoring-session/${id}`,
			providesTags: (result) =>
				result
					? [
							{
								type: "ProctoringSession" as const,
								id: result.id,
							},
						]
					: ["ProctoringSession"],
		}),
		getProctoringEvents: builder.query<
			ProctoringEventInfo,
			{
				courseId: number;
				sessionId: string;
			} & Partial<ProctoringEventQueryOptions>
		>({
			query: ({ courseId, sessionId }) => `quiz/event/${courseId}/${sessionId}`,
			transformResponse: ({ events, anomalies }: ProctoringEventsResponse, _, { flagLevels = [], eventTypes = [] }) => {
				const anomaliesById = Object.fromEntries(anomalies.map((anomaly) => [anomaly.id, anomaly]));

				const eventTypesSet = new Set(eventTypes);
				const eventTypeFilters = FILTER_EVENTS.filter(({ name }) => eventTypesSet.has(name));
				const eventsWithAnomalies = events.map((event) => ({
					...event,
					anomaly: event.anomalyRef ? anomaliesById[event.anomalyRef] : undefined,
				}));

				const startProctoringEvent = eventsWithAnomalies?.find(
					({ type, name }) => type === "session" && name === "start proctoring",
				);
				const startProctoringTime = startProctoringEvent ? new Date(startProctoringEvent.time).getTime() : undefined;
				const startQuizEvent = eventsWithAnomalies?.find(
					({ type, name }) => type === "session" && name === "start quiz",
				);
				const startQuizTime = startQuizEvent ? new Date(startQuizEvent.time).getTime() : undefined;
				const endQuizEvent = eventsWithAnomalies?.find(({ type, name }) => type === "session" && name === "end quiz");
				const endProctoringEvent = eventsWithAnomalies?.find(
					({ type, name }) => type === "session" && name === "end proctoring",
				);
				const startRecordingEvent = eventsWithAnomalies?.find(
					({ type, name }) => type === "session" && name === "start recording",
				);

				const convertedEvents = eventsWithAnomalies.map((event) => ({
					...event,
					time: new Date(event.time).getTime() - (startProctoringTime ?? 0),
				}));
				convertedEvents.sort((a, b) => a.time - b.time);

				return {
					events: convertedEvents
						.filter((event) => {
							const { time, type, name, anomaly } = event;
							return (
								(time >= (startQuizTime ?? 0) - (startProctoringTime ?? 0) ||
									(type === "system" && name === "block applications" && anomaly)) &&
								(flagLevels.length == 0 || flagLevels.includes(anomaly?.flagLevel ?? 0)) &&
								(eventTypes.length === 0 || eventTypeFilters.some(({ check }) => check(convertEventFormat(event))))
							);
						})
						.map((event) => ({
							...event,
							time: Math.max(event.time, (startQuizTime ?? 0) - (startProctoringTime ?? 0)),
						})),
					startProctoringTime,
					startQuizTime,
					endQuizTime: endQuizEvent ? new Date(endQuizEvent.time).getTime() : undefined,
					endProctoringTime: endProctoringEvent ? new Date(endProctoringEvent.time).getTime() : undefined,
					startRecordingTime: startRecordingEvent ? new Date(startRecordingEvent.time).getTime() : undefined,
					proctoringOptions: startProctoringEvent?.details?.options as
						| (ProctoringOption & ProctoringAssessmentConfigurationV1)
						| undefined,
					sessionEvents: convertedEvents.filter(({ type }) => type === "session"),
				};
			},
			providesTags: (result, _, { sessionId }) =>
				result
					? [
							...result.events.map((event) => ({
								type: "Event" as const,
								id: event.id,
							})),
							{ type: "Events" as const, id: sessionId },
						]
					: [{ type: "Events" as const, id: sessionId }, "Event" as const],
			onQueryStarted: async (_, { queryFulfilled, extra }) => {
				await queryFulfilled;
				(extra as { onDataUpdated?: () => void }).onDataUpdated?.();
			},
		}),
		resolveFlag: builder.mutation<void, { sessionId: string; eventId: number; comment?: string }>({
			query: ({ sessionId, eventId, comment }) => ({
				url: `quiz/event/${sessionId}/${eventId}/anomaly`,
				method: "PATCH",
				body: {
					resolved: true,
					comment,
				},
			}),
			invalidatesTags: (_, __, { eventId }) => [{ type: "Event" as const, id: eventId }],
		}),
		getAssessment: builder.query<Assessment, number>({
			queryFn: async (id, _api, _extra, fetchWithBQ) => {
				const result = (await fetchWithBQ(`assessment/${id}`)) as {
					data: {
						id: number;
						courseId: number;
						quizInfo?: { name?: string };
					};
				};
				const assessment: Assessment = { ...result.data, name: result.data.quizInfo?.name };
				const course = await apiClient.course.getCourseInfoFromId(assessment.courseId);
				assessment.course = { id: course.id, name: course.name ?? undefined };
				return { ...result, data: assessment };
			},
			providesTags: (result) => (result ? [{ type: "Assessment" as const, id: result.id }] : ["Assessment"]),
		}),
		getUser: builder.query<User, number>({
			query: (id) => `institute-user/${id}`,
			transformResponse: ({
				id,
				userInfo,
			}: {
				id: number;
				userInfo?: { profile_picture?: string; full_name?: string };
			}) => ({ id, name: userInfo?.full_name, profilePictureUrl: userInfo?.profile_picture }),
			providesTags: (result) => (result ? [{ type: "User" as const, id: result.id }] : ["User"]),
		}),
		getVideoUrls: builder.query<
			RecordingSourceVideoUrls,
			{
				sessionId: string;
				recordingType: "webcam" | "screencapture";
			}
		>({
			queryFn: async ({ sessionId, recordingType }, _api, _extra, fetchWithBQ) => {
				const result = await fetchWithBQ(`quiz/recording/url/${sessionId}/${recordingType}`);
				if (result.data) {
					const filteredVideoUrls = Object.fromEntries(
						await Promise.all(
							Object.entries(result.data as RecordingSourceVideoUrls).map(async ([index, sourceUrls]) => [
								index,
								Object.fromEntries(
									(
										await Promise.all(
											Object.entries(sourceUrls).map(async ([key, url]) => {
												try {
													if ((await fetch(url, { method: "HEAD" })).ok) {
														return [key, url];
													}
												} catch (e) {
													console.error(e);
												}
												return [key, undefined];
											}),
										)
									).filter(([, url]) => url),
								),
							]),
						),
					);
					return { ...result, data: filteredVideoUrls };
				}
				return result;
			},
			providesTags: ["VideoUrls"],
		}),
	}),
});

export default proctoringSessionApi;
